import { useContext, useState } from 'react';
import { AdminDashboardContext } from '../contexts';
import {
  useAssignedPatientsMetricsQuery,
  useCareNotesCreatedMetricsQuery,
  useCarePlanUpdatesMetricsQuery,
  usePatientUniqueUpdatesMetricsQuery,
} from '@common/cache/queries/metrics';
import useUserStore from 'store/user.store';

type patientChartsType = 'assigned' | 'unique' | 'careNotes' | 'carePlan';

const sortByInitialValue: Record<patientChartsType, 'ASC' | 'DESC'> = {
  assigned: 'DESC',
  unique: 'DESC',
  careNotes: 'DESC',
  carePlan: 'DESC',
};

export const usePatientCharts = () => {
  const [patientChartSortByKey, setPatientChartSortByKey] =
    useState(sortByInitialValue);
  const { user } = useUserStore();
  const { filters } = useContext(AdminDashboardContext);
  const { interval, to_date, from_date } = filters;

  const { data: assignedPatients, isLoading: isLoadingAssigned } =
    useAssignedPatientsMetricsQuery({
      interval,
      to_date,
      from_date,
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      sort_order: patientChartSortByKey.assigned,
    });
  const { data: uniquePatientUpdates, isLoading: isLoadingUnique } =
    usePatientUniqueUpdatesMetricsQuery({
      interval,
      to_date,
      from_date,
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      sort_order: patientChartSortByKey.unique,
    });
  const { data: careNotesAdded, isLoading: isLoadingCareNotes } =
    useCareNotesCreatedMetricsQuery({
      interval,
      to_date,
      from_date,
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      sort_order: patientChartSortByKey.careNotes,
    });
  const { data: carePlansUpdated, isLoading: isLoadingCarePlan } =
    useCarePlanUpdatesMetricsQuery({
      interval,
      to_date,
      from_date,
      supervisor_ids: filters.supervisor_ids,
      record_ids: filters.record_ids,
      sort_order: patientChartSortByKey.carePlan,
    });

  const hasViewPermission = user?.permissions.includes('view_reports');

  const handleChangeSortByKey = (key: patientChartsType) => {
    setPatientChartSortByKey(prevState => ({
      ...prevState,
      [key]: prevState[key] === 'ASC' ? 'DESC' : 'ASC',
    }));
  };

  return {
    hasViewPermission,
    assignedPatients,
    uniquePatientUpdates,
    careNotesAdded,
    carePlansUpdated,
    patientChartSortByKey,
    handleChangeSortByKey,
    isLoadingAssigned,
    isLoadingUnique,
    isLoadingCareNotes,
    isLoadingCarePlan,
  };
};
